import {
  localizedNumber,
  returnSingularOrPlural,
} from "../../../utils/help/localization/localization";
import {
  getLocalizedTierText,
  getTierRank,
} from "../../../utils/loyalty/tiers";
import {
  LoyaltyTierProgress,
  LoyaltyTierSystemTier,
} from "../../../utils/loyaltyTypes";
import { useAppSelector } from "../useReduxHooks";
import { useTranslationI18 } from "../useTranslationI18";

/** A tier condition line: the value is emphasised, the trailing copy is not. */
type LoyaltyTierRequirement = {
  value: string;
  suffix?: string;
};

/**
 * Single source of truth for the loyalty tier wording. Everything is driven by
 * loyalty-service (tier names, qualification unit type, points name, earning
 * rates) and the restaurant-configured currency.
 */
export const useLoyaltyTierCopy = () => {
  const { t, i18n } = useTranslationI18();
  const tierSystem = useAppSelector((state) => state.loyaltyTierSystem.data);
  const currency = useAppSelector((state) => state.config.currency);
  const configuredPointName = getLocalizedTierText(
    tierSystem?.pointsName,
    i18n.language
  );

  // loyalty-service sends `pointsName` as a singular noun, so it has to be
  // pluralised the same way the legacy pointing UI does (English only). A chain
  // can configure it already plural though, and the shared helper appends an "s"
  // regardless - which is where "0.7 pointss" came from.
  const getPointLabel = (count: number) => {
    if (!configuredPointName) {
      return t(`pointing-system:${count === 1 ? "point" : "points"}`);
    }

    const configured = configuredPointName.trim();

    return /s$/i.test(configured)
      ? configured
      : returnSingularOrPlural(configured, count) ?? configured;
  };

  const getUnitLabel = (count: number) =>
    tierSystem?.tierSystemUnitType === "POINTS"
      ? getPointLabel(count)
      : t(`pointing-system:${count === 1 ? "order" : "orders"}`);

  const getTierName = (tier?: LoyaltyTierSystemTier) =>
    getLocalizedTierText(tier?.tierName, i18n.language);

  // A chain can name its currency anything ("beans"), and shortening that to
  // "pts" would be wrong - but loyalty-service also sends the stock word
  // ("point"), which does have a short form. Comparing against the locale's own
  // wording tells the two apart.
  const usesStockPointName =
    !configuredPointName ||
    [t("pointing-system:point"), t("pointing-system:points")].some(
      (label) =>
        label.toLowerCase() === configuredPointName.trim().toLowerCase()
    );

  /**
   * Figma switches the threshold's unit to its short form once the number
   * reaches four digits, so the number itself keeps its full size.
   */
  const getUnitLabelForValue = (count: number) =>
    tierSystem?.tierSystemUnitType === "POINTS" &&
    usesStockPointName &&
    count >= 1000
      ? t("pointing-system:points-short")
      : getUnitLabel(count);

  const getUnitsValue = (count: number) =>
    `${localizedNumber(count)} ${getUnitLabel(count)}`;

  const getEarningRateText = (tier: LoyaltyTierSystemTier) =>
    t("pointing-system:points-per-currency", {
      currency,
      count: localizedNumber(tier.earningRate || 0),
      points: getPointLabel(tier.earningRate || 0),
    });

  const getOutOfUnitsText = (count: number) =>
    t("pointing-system:out-of-tier-units", {
      count: localizedNumber(count),
      unit: getUnitLabel(count),
    });

  /** Condition lines shown on the member's own tier card, in Figma order. */
  const getCurrentTierRequirements = (
    tier: LoyaltyTierSystemTier,
    sortedTiers: LoyaltyTierSystemTier[],
    tierProgress?: LoyaltyTierProgress
  ): LoyaltyTierRequirement[] => {
    const requirements: LoyaltyTierRequirement[] = [];
    const nextTier = sortedTiers.find(
      (candidate) => candidate.tierOrder > tier.tierOrder
    );
    const isLowestTier = getTierRank(tier, sortedTiers) === 1;

    if (tierProgress) {
      // Which conditions a tier states follows its position, not whether they
      // happen to be satisfied: the first tier can only be graduated from, the
      // last can only be held, and every tier between them says both.
      if (!isLowestTier) {
        requirements.push({
          // The tier's own requirement, not what is left of it. This tier system
          // qualifies on the live balance (useCurrentPoints), so the remainder
          // reads 0 for anyone already holding enough - "550 points to stay in
          // Gold" states the bar itself, which is what the card is for.
          value: getUnitsValue(tier.minUnitsToStay || 0),
          suffix: t("pointing-system:tier-to-stay", {
            tier: getTierName(tier),
          }),
        });
      }
      if (tierProgress.remainingUnitsToUpgrade !== null && nextTier) {
        requirements.push({
          value: getUnitsValue(tierProgress.remainingUnitsToUpgrade),
          suffix: t("pointing-system:tier-to-graduate", {
            tier: getTierName(nextTier),
          }),
        });
      }
      // loyalty-service counts these days off customer_tier_summary.endsAt,
      // stamped as now + the tier's coolDownDays when the member enters or
      // upgrades a tier: it is the countdown to the tier review, not to an
      // upgrade and not to points expiry (that arrives as pointsExpiry). A tier
      // configured without a cooldown has no period to count down and comes
      // back as 0 or null, and nothing is shown in its place.
      const daysToReview = tierProgress.remainingDaysToResetCooldownPeriod;

      if (typeof daysToReview === "number" && daysToReview > 0) {
        requirements.push({
          value: t(
            `pointing-system:${daysToReview === 1 ? "day-left" : "days-left"}`,
            { count: localizedNumber(daysToReview) }
          ),
        });
      }
    } else if (nextTier) {
      requirements.push({
        value: getUnitsValue(nextTier.minUnitsToEnter || 0),
        suffix: t("pointing-system:tier-to-graduate", {
          tier: getTierName(nextTier),
        }),
      });
    }

    if (tier.earningRate) {
      requirements.push({ value: getEarningRateText(tier) });
    }

    return requirements;
  };

  return {
    getPointLabel,
    getUnitLabel,
    getUnitLabelForValue,
    getUnitsValue,
    getTierName,
    getEarningRateText,
    getOutOfUnitsText,
    getCurrentTierRequirements,
  };
};
